import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

import HeaderImage from 'assets/resources/scenery-reconstruction/header-section.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title: 'Building Real Worlds in Simulation: Scenery Reconstruction for Autonomous Systems',
  publishDate: 'June 2, 2026',
  minutesOfRead: 6,
};

export const secondJune2026ArticleMeta: PageMeta = {
  url: '/articles/scenery-reconstruction-autonomous-systems',
  title: commonData.title,
  description:
    'Scenery reconstruction turns real-world geospatial data into accurate 3D environments, so autonomous systems can train and test against the specific places they will operate in, not just plausible ones.',
};

export const secondJune2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'Scenery Reconstruction for Autonomous Systems',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          <em>
            Why accurate digital environments are becoming a competitive advantage in AI development
          </em>
        </p>
        <p>
          For teams building autonomous systems, the gap between a simulation that looks plausible
          and one that reflects a specific real location is where engineering decisions are won or
          lost. Scenery reconstruction closes that gap.
        </p>
      </>
    ),
    ...commonData,
  },

  {
    type: 'common',
    title: 'What Scenery Reconstruction Actually Means',
    text: (
      <>
        <p>
          Scenery reconstruction is the process of creating 3D models of real-world landscapes from
          geospatial data. If you work with autonomous systems, whether drones, aircraft, or ground
          vehicles, you have probably heard related terms like terrain generation, procedural
          landscapes, or digital twins. They all orbit the same idea: build a digital version of a
          physical environment so you can train and test in it without leaving your desk.
        </p>
        <p>
          The distinction matters, though. Terrain generation often refers to procedurally created
          landscapes that look plausible but do not correspond to any real location. Scenery
          reconstruction, by contrast, starts with actual data from the real world. It aims to
          reproduce a specific place with enough accuracy that the simulation becomes useful for
          engineering decisions, not just visual demonstrations.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Why Scenery Reconstruction Has Become a Bottleneck',
    text: (
      <>
        <p>
          Autonomous systems in defense, aviation, and industrial automation fail in the same place:
          rare conditions tied to specific real locations that real-world data collection cannot
          reach at scale. Without an accurate digital version of those locations, teams cannot
          generate the synthetic data or run the edge-case scenarios that operational readiness
          depends on.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Where It Fits in the AILiveSim Platform',
    text: (
      <>
        <p>
          Scenery reconstruction is the foundation layer the rest of the platform builds on, feeding
          directly into multi-sensor simulation, the Automated Training Pipeline (ATP) that fills
          coverage gaps with synthetic data, and Intelligent System Testing (IST) for scenario-based
          validation. Because the reconstructed environment lives inside the same pipeline as
          scenario authoring, sensor models, and Simulation-in-the-Loop (SiL) execution, a new
          location can move from raw geospatial inputs to a closed-loop test ready for the
          customer&apos;s own AI stack without leaving the platform.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Where The Data Comes From',
    text: (
      <>
        <p>
          Scenery reconstruction draws on several types of input rather than a single source.
          Satellite imagery, aerial photography, LiDAR scans, and commercial geospatial datasets can
          all feed the same workflow, and most projects combine at least two of them.
        </p>
        <p>
          The main inputs fall into a few categories. Raster imagery (satellite or aerial) provides
          the visual surface and helps classify land cover. Elevation comes from its own data layer,
          typically a digital elevation model or a LiDAR point cloud, since it cannot reliably be
          inferred from a 2D image. Vector data adds structured features: road networks, building
          footprints, water bodies, administrative boundaries, represented as lines, points, and
          polygons.
        </p>
        <p>
          Some of this is open. OpenStreetMap covers roads, boundaries, and points of interest in
          much of the world, and public agencies release elevation and land cover datasets at
          varying resolutions. Other inputs are commercial: high-resolution satellite imagery, recent
          aerial surveys, and detailed elevation models tend to sit behind paid licenses. Teams
          typically mix both, using open data for breadth and commercial data where precision matters
          for the specific region or feature being modelled.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'How AILiveSim Builds Reconstructed Environments',
    text: (
      <>
        <p>
          AILiveSim&apos;s approach to scenery reconstruction starts with satellite imagery (but is
          not the only method) and injects publicly available data from sources like OpenStreetMap
          into its scenery reconstruction pipeline. The system gathers the geospatial features it
          needs, processes them, and assembles a 3D environment that reflects the real location&apos;s
          topology, vegetation patterns, and built structures.
        </p>
        <p>
          What makes this approach different from manual environment creation is both speed and
          fidelity. Building a comparable environment by hand would take roughly two months of
          skilled labor, and the result would likely be less accurate than what the automated
          pipeline produces.
        </p>
        <p>
          The environments are also customizable. Teams can strip down terrain to its underlying
          geology, develop a landscape over time to simulate seasonal changes, or scale the area of
          interest from a single facility to an entire region. Because the reconstruction pipeline is
          integrated directly into the AILiveSim platform, the resulting environments are immediately
          ready for scenario creation, sensor simulation, and AI testing workflows.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Benefits: Why This Matters for Autonomous System Development',
    text: (
      <>
        <p>
          The most straightforward benefit is time. What once took months of manual modeling now
          takes a fraction of that. For teams running tight development schedules, this alone can
          shift project timelines.
        </p>
        <p>
          Cost savings follow from the time reduction, but they also come from reduced field testing.
          When you can reconstruct the exact terrain your drone or vehicle will operate in, you can
          run thousands of simulated missions before committing to a single physical test flight.
          Edge cases that would be expensive or dangerous to create in the real world, like testing
          drone navigation in mountainous terrain during fog, become routine simulation runs.
        </p>
        <p>
          There is also a quality argument. Reconstructed terrains built from real data are more
          representative of actual operating conditions than hand-modeled or procedurally generated
          alternatives. This means the AI models trained in these environments are more likely to
          perform well when they encounter the real thing.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Use Cases: Who Benefits and How',
    text: (
      <>
        <p>
          Scenery reconstruction is a tool you build with, not a fixed product you receive.
          Vision-driven organizations typically already hold significant amounts of geospatial and
          operational data, and the platform is designed to put that data to work rather than replace
          it. If you have a specific operation to plan or rehearse, you can bring your own data
          sources into the simulator and build the environment around them. AILiveSim currently
          supports a range of public and commercial data inputs and will continue to expand that
          list, with a deliberate focus on simplifying the path from raw data to a usable simulation
          environment.
        </p>
        <p>
          Defense organizations training drone fleets across different countries are an obvious fit.
          Instead of deploying teams to each location for data collection, they reconstruct the target
          environment from available geospatial data and run their training scenarios in simulation.
          The same principle applies to civilian applications: urban air mobility companies testing
          navigation in specific city corridors, or infrastructure operators simulating inspection
          routes over complex industrial sites.
        </p>
        <p>
          For teams already working with synthetic data, scenery reconstruction fills a gap that
          procedural generation cannot. It gives you a specific, verifiable place to test in, not just
          a plausible one. And when that place needs to change, whether due to construction, seasonal
          variation, or simply a different area of operations, the reconstruction pipeline can produce
          the updated environment without starting from scratch.
        </p>
      </>
    ),
  },
];

export const secondJune2026ArticleCard: TCardProps = {
  description:
    'Scenery reconstruction turns real-world geospatial data into accurate 3D environments, so autonomous systems can train and test against the specific places they will operate in, not just plausible ones.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: secondJune2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.AISimulation,
    EHashtag.DigitalTwin,
    EHashtag.SyntheticData,
    EHashtag.AutonomousVehicles,
    EHashtag.DefenseTechnology,
  ],
};
